import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, Wand2, Archive, CheckCircle2, Trash2, RotateCcw, Loader2 } from "lucide-react";

/**
 * Founder-only maintenance console.
 * Founder Directive 2026-02-09 — Production Data Reset.
 *
 * Two panels:
 *   1. Contamination scan (read-only preview of what would be moved).
 *   2. Review queue — records already tagged as test/review awaiting a decision.
 */
export default function Maintenance() {
  const { user } = useAuth();
  const isFounder = (user?.role === "founder") || (user?.role === "admin");

  const [scan, setScan] = useState(null);
  const [queue, setQueue] = useState([]);
  const [queueFilter, setQueueFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executeResult, setExecuteResult] = useState(null);

  const loadScan = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/maintenance/scan");
      setScan(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      const { data } = await api.get("/maintenance/review-queue", { params: { status: queueFilter } });
      setQueue(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }, [queueFilter]);

  useEffect(() => {
    if (isFounder) {
      loadScan();
      loadQueue();
    }
  }, [isFounder, loadScan, loadQueue]);

  const runExecute = async () => {
    const ok = window.confirm(
      "Tag every record with its environment (production / test / review) and move flagged records to the Founder Review queue.\n\n" +
      "No records will be deleted. This is reversible.\n\nProceed?"
    );
    if (!ok) return;
    try {
      setExecuting(true);
      const { data } = await api.post("/maintenance/execute", {});
      setExecuteResult(data);
      toast.success(`Reset complete. ${data.queue_added} record(s) moved to review.`);
      await Promise.all([loadScan(), loadQueue()]);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setExecuting(false);
    }
  };

  const actOn = async (queueId, verb) => {
    if (verb === "delete") {
      const ok = window.confirm("Permanently delete this record from the database? This cannot be undone.");
      if (!ok) return;
    }
    try {
      await api.post(`/maintenance/review/${queueId}/${verb}`);
      toast.success(`Record ${verb === "promote" ? "restored to production" : verb}d.`);
      loadQueue();
      loadScan();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const totals = scan?.totals || { production: 0, test: 0, review: 0 };
  const perCollection = useMemo(() => {
    if (!scan) return [];
    return Object.entries(scan.collections)
      .map(([k, v]) => ({ name: k, ...v }))
      .sort((a, b) => (b.test + b.review) - (a.test + a.review));
  }, [scan]);

  if (!isFounder) {
    return (
      <div className="max-w-lg mx-auto p-8 border border-line rounded-md bg-bone">
        <div className="flex items-center gap-3 text-terracotta">
          <ShieldAlert className="h-5 w-5" />
          <div className="font-serif text-xl">Founder access required</div>
        </div>
        <p className="text-sm text-ink-soft mt-3">
          The Maintenance console is restricted to the Founder role.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="maintenance-page">
      <div>
        <div className="flex items-baseline gap-3">
          <h1 className="font-serif text-3xl text-ink">Maintenance · Environment Separation</h1>
          <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em]">Founder only</Badge>
        </div>
        <p className="text-sm text-ink-soft mt-2 max-w-3xl">
          Every founder-facing page defaults to <span className="font-semibold">environment = production</span>.
          Records classified as <span className="font-semibold">test</span> or <span className="font-semibold">review</span> are
          hidden from the workspace and land in the queue below for your decision.
        </p>
      </div>

      {/* --- Contamination scan --- */}
      <section className="border border-line rounded-md bg-bone">
        <div className="p-5 border-b border-line flex items-center justify-between gap-3">
          <div>
            <div className="font-serif text-lg text-ink">Contamination Scan</div>
            <div className="text-xs text-ink-soft mt-1">
              Read-only preview. No changes are made until you run the reset.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadScan}
              disabled={loading}
              data-testid="maintenance-scan-refresh"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
              Refresh scan
            </Button>
            <Button
              onClick={runExecute}
              disabled={executing}
              data-testid="maintenance-execute-btn"
              className="bg-forest text-bone hover:bg-forest/90"
            >
              {executing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1" />}
              Tag environments &amp; move to review
            </Button>
          </div>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Production" value={totals.production} tone="ok" testid="maintenance-stat-production" />
          <StatCard label="Test residue" value={totals.test} tone="warn" testid="maintenance-stat-test" />
          <StatCard label="Needs review" value={totals.review} tone="review" testid="maintenance-stat-review" />
        </div>

        <div className="px-5 pb-5">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.14em] text-ink-soft border-b border-line">
                <tr>
                  <th className="text-left py-2 pr-4">Collection</th>
                  <th className="text-right py-2 pr-4">Total</th>
                  <th className="text-right py-2 pr-4">Production</th>
                  <th className="text-right py-2 pr-4">Test</th>
                  <th className="text-right py-2 pr-4">Review</th>
                  <th className="text-left py-2">Sample flagged</th>
                </tr>
              </thead>
              <tbody>
                {perCollection.map((row) => (
                  <tr key={row.name} className="border-b border-line/60">
                    <td className="py-2 pr-4 font-mono text-xs">{row.name}</td>
                    <td className="py-2 pr-4 text-right">{row.total}</td>
                    <td className="py-2 pr-4 text-right text-forest">{row.production}</td>
                    <td className="py-2 pr-4 text-right text-terracotta">{row.test}</td>
                    <td className="py-2 pr-4 text-right text-ink-soft">{row.review}</td>
                    <td className="py-2 text-xs text-ink-soft">
                      {[...(row.samples_test || []), ...(row.samples_review || [])]
                        .slice(0, 2)
                        .map((s) => s.name || s.id || "(unnamed)")
                        .join(" · ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {executeResult && (
          <div className="mx-5 mb-5 p-3 bg-forest/5 border border-forest/20 rounded text-xs text-ink-soft">
            <div className="font-medium text-ink">Last reset — {new Date(executeResult.as_of).toLocaleString()}</div>
            <div className="mt-1">Queue additions: <span className="font-semibold">{executeResult.queue_added}</span> · Collections touched: <span className="font-semibold">{Object.keys(executeResult.after || {}).length}</span></div>
          </div>
        )}
      </section>

      {/* --- Review queue --- */}
      <section className="border border-line rounded-md bg-bone">
        <div className="p-5 border-b border-line flex items-center justify-between">
          <div>
            <div className="font-serif text-lg text-ink">Founder Review Queue</div>
            <div className="text-xs text-ink-soft mt-1">
              Records awaiting your decision. Promote to make visible; archive to hide; delete to remove permanently.
            </div>
          </div>
          <div className="flex gap-2 text-xs">
            {["pending", "promoted", "archived", "deleted", "all"].map((s) => (
              <button
                key={s}
                onClick={() => setQueueFilter(s)}
                data-testid={`maintenance-queue-filter-${s}`}
                className={`px-3 py-1 rounded-full transition-colors ${
                  queueFilter === s ? "bg-forest text-bone" : "bg-white text-ink-soft hover:bg-line"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {queue.length === 0 ? (
          <div className="p-10 text-center text-sm text-ink-soft">
            No records in this state. Run the scan &amp; reset above to populate the queue.
          </div>
        ) : (
          <ul className="divide-y divide-line/70">
            {queue.map((entry) => (
              <li key={entry.id} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`maintenance-queue-item-${entry.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <Badge variant={entry.classification === "test" ? "destructive" : "outline"} className="text-[10px] uppercase tracking-[0.14em]">
                      {entry.classification}
                    </Badge>
                    <span className="font-mono text-ink-soft">{entry.collection}</span>
                    <span className="text-ink-soft/70">·</span>
                    <span className="text-ink-soft">{entry.reason}</span>
                  </div>
                  <div className="mt-1 font-medium text-sm text-ink truncate">
                    {entry.record_name || <span className="italic text-ink-soft">(no name)</span>}
                  </div>
                  <div className="mt-1 text-xs text-ink-soft/80 font-mono truncate">
                    id: {entry.record_id}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {entry.status === "pending" ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => actOn(entry.id, "promote")}
                        data-testid={`maintenance-promote-${entry.id}`}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Promote
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => actOn(entry.id, "archive")}
                        data-testid={`maintenance-archive-${entry.id}`}
                      >
                        <Archive className="h-3.5 w-3.5 mr-1" /> Archive
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-terracotta border-terracotta/30 hover:bg-terracotta/10"
                        onClick={() => actOn(entry.id, "delete")}
                        data-testid={`maintenance-delete-${entry.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                      </Button>
                    </>
                  ) : (
                    <Badge variant="outline" className="text-[10px] uppercase">
                      {entry.status}
                    </Badge>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const toneClass = {
    ok: "bg-forest/5 border-forest/30 text-forest",
    warn: "bg-terracotta/10 border-terracotta/40 text-terracotta",
    review: "bg-bone border-line text-ink-soft",
  }[tone];
  return (
    <div className={`p-4 rounded-md border ${toneClass}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-medium">{label}</div>
      <div className="text-3xl font-serif mt-2">{value}</div>
    </div>
  );
}
